import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Draft } from "immer";
import { ICompanyServicePlan } from "modules/company/types";
import { ITheater } from "types/theater";
import actionTypes from "./constants";
import { IEvent } from "./types";

export type EventState = {
  events: Record<string, IEvent>;
  currentEventId: string | null;
  pastMyEventIds: string[];
  ongoingMyEventIds: string[];
  upcomingMyEventIds: string[];
  pastJoinedEventIds: string[];
  ongoingJoinedEventIds: string[];
  upcomingJoinedEventIds: string[];
  newEventId: string | null;
  currentEventCompanyPlan: ICompanyServicePlan | null;
  isZoomEnabled: boolean;
};

export const initialState: EventState = {
  events: {},
  currentEventId: null,
  pastMyEventIds: [],
  ongoingMyEventIds: [],
  upcomingMyEventIds: [],
  pastJoinedEventIds: [],
  ongoingJoinedEventIds: [],
  upcomingJoinedEventIds: [],
  newEventId: null, // newly created event to show success popup
  currentEventCompanyPlan: null, // current event's company plan, for company feature toggles
  isZoomEnabled: false,
};

const addEventToState = (state: Draft<EventState>, newEvent: IEvent) => {
  let updatedNewEvent = newEvent;
  const oldEvent = state.events[newEvent.id];

  if (oldEvent) {
    let newTheaters: ITheater[];
    const { theaters: oldTheaters } = oldEvent;

    if (newEvent.theaters) {
      newTheaters = newEvent.theaters.map(
        (theater: ITheater, index: number) => {
          if (typeof theater !== "string") {
            // If new data has details, then merge old and new
            return { ...oldTheaters?.[index], ...theater };
          }

          // New data has only ID as string
          // And old data has all details - return only old details
          if (
            typeof oldTheaters?.[index] !== "string" &&
            oldTheaters?.[index].id === theater
          ) {
            return { ...oldTheaters[index] };
          }

          return theater;
        },
      );
    } else {
      newTheaters = oldTheaters as ITheater[];
    }
    updatedNewEvent = {
      ...oldEvent,
      ...newEvent,
      // directory id can be missing on incoming object but present in old
      directoryId: newEvent.directoryId,
      theaters: newTheaters,
    };
  }

  state.events[newEvent.id] = updatedNewEvent;
};

export const eventSlice = createSlice({
  name: "event",
  initialState,
  reducers: {
    setNewEventId: (state, action: PayloadAction<string | null>) => {
      state.newEventId = action.payload;
    },
    setPastMyEventIds: (state, action: PayloadAction<string[]>) => {
      state.pastMyEventIds = action.payload;
    },
    setOngoingMyEventIds: (state, action: PayloadAction<string[]>) => {
      state.ongoingMyEventIds = action.payload;
    },
    setUpcomingMyEventIds: (state, action: PayloadAction<string[]>) => {
      state.upcomingMyEventIds = action.payload;
    },
    setPastJoinedEventIds: (state, action: PayloadAction<string[]>) => {
      state.pastJoinedEventIds = action.payload;
    },
    setOngoingJoinedEventIds: (state, action: PayloadAction<string[]>) => {
      state.ongoingJoinedEventIds = action.payload;
    },
    setUpcomingJoinedEventIds: (state, action: PayloadAction<string[]>) => {
      state.upcomingJoinedEventIds = action.payload;
    },
    addEventDetails: (state, action: PayloadAction<IEvent>) => {
      const { payload: newEvent } = action;

      addEventToState(state, newEvent);
    },
    addAllEventDetails: (state, action: PayloadAction<IEvent[]>) => {
      action.payload.forEach((newEvent) => addEventToState(state, newEvent));
    },
    removeEvent: (state, action: PayloadAction<string>) => {
      const { payload: eventId } = action;

      delete state.events[eventId];

      state.pastMyEventIds = state.pastMyEventIds.filter(
        (id) => id !== eventId,
      );

      state.ongoingMyEventIds = state.ongoingMyEventIds.filter(
        (id) => id !== eventId,
      );

      state.upcomingMyEventIds = state.upcomingMyEventIds.filter(
        (id) => id !== eventId,
      );
    },
    resetEvents: (state) => ({
      ...initialState,
      currentEventId: state.currentEventId,
      newEventId: state.newEventId,
    }),
    setCurrentEventId: (state, action: PayloadAction<string | null>) => {
      state.currentEventId = action.payload;
    },
    updateEventSpeakers: (
      state,
      action: PayloadAction<{ eventId: string; speakers: string[] }>,
    ) => {
      const {
        payload: { eventId, speakers },
      } = action;

      state.events[eventId].speakers = speakers;
    },
    updateCurrentEventCompanyPlan: (
      state,
      action: PayloadAction<ICompanyServicePlan | null>,
      // eslint-disable-next-line consistent-return
    ) => {
      if (!state.currentEventId || !action.payload) return state;

      state.currentEventCompanyPlan = action.payload;
    },
    setIsZoomEnabled: (state) => {
      state.isZoomEnabled = true;
    },
  },
});

export const {
  setNewEventId,
  setPastMyEventIds,
  setOngoingMyEventIds,
  setUpcomingMyEventIds,
  setPastJoinedEventIds,
  setOngoingJoinedEventIds,
  setUpcomingJoinedEventIds,
  removeEvent,
  addEventDetails,
  addAllEventDetails,
  resetEvents,
  setCurrentEventId,
  updateEventSpeakers,
  updateCurrentEventCompanyPlan,
  setIsZoomEnabled,
} = eventSlice.actions;

export const viewEvent = (eventId: string) => ({
  type: actionTypes.VIEW_EVENT,
  payload: { eventId },
});

export default eventSlice.reducer;
